/**
 * Badge System API
 *
 * Provides functions for managing badges, user badges, and streak tracking.
 */

import { supabase } from './supabase';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/**
 * Badge category (e.g., Consistency, Time-Based, Challenges)
 */
export interface BadgeCategory {
  id: string;
  name: string;
  description: string | null;
  // optional active flag for categories — some DBs may not have this column
  active?: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Individual badge that can be earned
 */
export interface Badge {
  id: string;
  category_id: string;
  name: string;
  image_url?: string | null;
  color?: string | null;
  description: string | null;
  requirement_type: string;
  requirement_value: number;
  /** Numeric score for this badge; used to calculate user's top score */
  score: number;
  icon_key: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Badge earned by a user
 */
export interface UserBadge {
  id: string;
  user_id: string;
  badge_id: string;
  earned_at: string;
  notified: boolean;
}

/**
 * User's streak tracking data
 */
export interface UserStreak {
  user_id: string;
  current_streak: number;
  longest_streak: number;
  last_session_date: string | null;
  updated_at: string;
}

/**
 * Combined badge data with category information
 */
export interface BadgeWithCategory extends Badge {
  category?: BadgeCategory;
}

/**
 * User badge with full badge details
 */
export interface UserBadgeWithDetails extends UserBadge {
  badge?: BadgeWithCategory;
}

// ============================================================================
// Badge Category Functions
// ============================================================================

/**
 * Fetch all badge categories
 */
export async function fetchBadgeCategories(): Promise<BadgeCategory[]> {
  const { data, error } = await supabase
    .from('badge_categories')
    .select('*')
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching badge categories:', error);
    throw new Error(`Failed to fetch badge categories: ${error.message}`);
  }

  return (data as BadgeCategory[]) || [];
}

// ============================================================================
// Badge Functions
// ============================================================================

/**
 * Fetch all active badges
 */
export async function fetchAllBadges(): Promise<Badge[]> {
  const { data, error } = await supabase
    .from('badges')
    .select('*')
    .eq('active', true)
    .order('requirement_value', { ascending: true });

  if (error) {
    console.error('Error fetching badges:', error);
    throw new Error(`Failed to fetch badges: ${error.message}`);
  }

  return (data as Badge[]) || [];
}

/**
 * Fetch all badges with their category information
 */
export async function fetchAllBadgesWithCategories(): Promise<
  BadgeWithCategory[]
> {
  const { data, error } = await supabase
    .from('badges')
    .select(
      `
      *,
      category:badge_categories(*)
    `
    )
    .eq('active', true)
    .order('requirement_value', { ascending: true });

  if (error) {
    console.error('Error fetching badges with categories:', error);
    throw new Error(`Failed to fetch badges with categories: ${error.message}`);
  }

  return (data as BadgeWithCategory[]) || [];
}

/**
 * Fetch badges by category
 */
export async function fetchBadgesByCategory(
  categoryId: string
): Promise<Badge[]> {
  const { data, error } = await supabase
    .from('badges')
    .select('*')
    .eq('category_id', categoryId)
    .eq('active', true)
    .order('requirement_value', { ascending: true });

  if (error) {
    console.error('Error fetching badges by category:', error);
    throw new Error(`Failed to fetch badges by category: ${error.message}`);
  }

  return (data as Badge[]) || [];
}

// ============================================================================
// User Badge Functions
// ============================================================================

/**
 * Fetch all badges earned by a specific user
 */
export async function fetchUserBadges(userId: string): Promise<UserBadge[]> {
  const { data, error } = await supabase
    .from('user_badges')
    .select('*')
    .eq('user_id', userId)
    .order('earned_at', { ascending: false });

  if (error) {
    console.error('Error fetching user badges:', error);
    throw new Error(`Failed to fetch user badges: ${error.message}`);
  }

  return (data as UserBadge[]) || [];
}

/**
 * Fetch user badges with full badge details
 */
export async function fetchUserBadgesWithDetails(
  userId: string
): Promise<UserBadgeWithDetails[]> {
  const { data, error } = await supabase
    .from('user_badges')
    .select(
      `
      *,
      badge:badges(
        *,
        category:badge_categories(*)
      )
    `
    )
    .eq('user_id', userId)
    .order('earned_at', { ascending: false });

  if (error) {
    console.error('Error fetching user badges with details:', error);
    throw new Error(
      `Failed to fetch user badges with details: ${error.message}`
    );
  }

  return (data as UserBadgeWithDetails[]) || [];
}

/**
 * Compute the highest score from an array of user badge details.
 * Returns 0 when no badges are present.
 */
export function computeHighestBadgeScore(
  userBadges: UserBadgeWithDetails[] | null | undefined
): number {
  if (!userBadges || userBadges.length === 0) return 0;
  let max = 0;
  for (const ub of userBadges) {
    const score = ub?.badge?.score ?? 0;
    if (score > max) max = score;
  }
  return max;
}

/**
 * Get the color for a user's highest badge.
 *
 * The function queries `user_badges` joined with `badges` and returns the
 * color of the highest badge the user has unlocked. It prefers `level` when
 * available, otherwise falls back to `score`.
 */
export async function getUserProfileBadgeColor(
  userId: string
): Promise<{ color: string | null; value: number } | null> {
  if (!userId) return null;

  const { data, error } = await supabase
    .from('user_badges')
    .select(`*, badge:badges(color, level, score)`)
    .eq('user_id', userId);

  if (error) {
    // If access is restricted via RLS or other DB reasons, don't throw —
    // we treat color as unavailable for the profile page.
    console.debug('getUserProfileBadgeColor supabase error:', error);
    return null;
  }

  if (!data || data.length === 0) return null;

  // Determine the "weight" of a badge candidate using level first, then
  // score. If neither exist, treat weight as 0.
  let best: { color?: string | null; value: number } | null = null;

  for (const ub of data as UserBadgeWithDetails[]) {
    const b = ub?.badge as BadgeWithCategory | undefined;
    if (!b) continue;

    let levelVal = Number.NaN;
    if (b && 'level' in b) {
      const lv = (b as unknown as { level?: unknown }).level;
      const parsed = Number(lv);
      if (Number.isFinite(parsed)) levelVal = parsed;
    }
    const scoreVal = Number.isFinite(Number(b.score))
      ? Number(b.score)
      : Number.NaN;

    const weight = !Number.isNaN(levelVal)
      ? levelVal
      : !Number.isNaN(scoreVal)
        ? scoreVal
        : 0;

    if (!best || weight > best.value) {
      best = { color: b.color ?? null, value: weight };
    }
  }

  if (!best) return null;
  const clr = best.color;
  const val = best.value ?? 0;
  return {
    color: clr && String(clr).trim() !== '' ? String(clr).trim() : null,
    value: val,
  };
}

/**
 * Fetch unnotified badges for a user (badges the user hasn't been shown yet)
 */
export async function fetchUnnotifiedBadges(
  userId: string
): Promise<UserBadgeWithDetails[]> {
  const { data, error } = await supabase
    .from('user_badges')
    .select(
      `
      *,
      badge:badges(
        *,
        category:badge_categories(*)
      )
    `
    )
    .eq('user_id', userId)
    .eq('notified', false)
    .order('earned_at', { ascending: true });

  if (error) {
    console.error('Error fetching unnotified badges:', error);
    throw new Error(`Failed to fetch unnotified badges: ${error.message}`);
  }

  return (data as UserBadgeWithDetails[]) || [];
}

/**
 * Mark a badge as notified
 */
export async function markBadgeNotified(userBadgeId: string): Promise<void> {
  const { error } = await supabase
    .from('user_badges')
    .update({ notified: true })
    .eq('id', userBadgeId);

  if (error) {
    console.error('Error marking badge as notified:', error);
    throw new Error(`Failed to mark badge as notified: ${error.message}`);
  }
}

/**
 * Mark multiple badges as notified
 */
export async function markBadgesNotified(
  userBadgeIds: string[]
): Promise<void> {
  if (userBadgeIds.length === 0) return;

  const { error } = await supabase
    .from('user_badges')
    .update({ notified: true })
    .in('id', userBadgeIds);

  if (error) {
    console.error('Error marking badges as notified:', error);
    throw new Error(`Failed to mark badges as notified: ${error.message}`);
  }
}

// ============================================================================
// User Streak Functions
// ============================================================================

/**
 * Fetch streak data for a specific user
 */
export async function fetchUserStreak(
  userId: string
): Promise<UserStreak | null> {
  const { data, error } = await supabase
    .from('user_streaks')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    // When no row exists maybeSingle will not return an error — but if we get
    // another error, surface it.
    if ('code' in error && error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching user streak:', error);
    throw new Error(`Failed to fetch user streak: ${error.message}`);
  }

  return data as UserStreak;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Group badges by category
 */
export function groupBadgesByCategory(
  badges: BadgeWithCategory[]
): Map<string, BadgeWithCategory[]> {
  const grouped = new Map<string, BadgeWithCategory[]>();

  badges.forEach((badge) => {
    const categoryName = badge.category?.name || 'Unknown';
    if (!grouped.has(categoryName)) {
      grouped.set(categoryName, []);
    }
    grouped.get(categoryName)!.push(badge);
  });

  return grouped;
}

/**
 * Check if a user has earned a specific badge
 */
export function hasEarnedBadge(
  userBadges: UserBadge[],
  badgeId: string
): boolean {
  return userBadges.some((ub) => ub.badge_id === badgeId);
}

export const FOURTEEN_DAY_STREAK_BADGE_ID =
  'b1ab0b35-041b-482d-8f0c-f7a3989d49bb';

export function has14DayStreakBadge(
  userBadges:
    | Array<
        | Pick<UserBadge, 'badge_id'>
        | Pick<UserBadgeWithDetails, 'badge_id' | 'badge'>
      >
    | null
    | undefined
): boolean {
  if (!userBadges || userBadges.length === 0) return false;

  for (const ub of userBadges) {
    const badgeIdFromDetails = 'badge' in ub ? (ub.badge?.id ?? null) : null;

    if (badgeIdFromDetails === FOURTEEN_DAY_STREAK_BADGE_ID) return true;

    // Fallback for callers that only have `badge_id`.
    if (ub.badge_id === FOURTEEN_DAY_STREAK_BADGE_ID) return true;
  }

  return false;
}

/**
 * Get badge icon (returns emoji or fallback)
 */
export function getBadgeIcon(badge: Badge): string {
  return badge.icon_key || '🏅';
}
